import { isOffline } from "../facade/ServiceFacade";
import { RISTA_AI_V2_PROVIDER } from "./constants";
import { ENGINE_V2, getRecommendationEngineVersion } from "./engine";
import type { RecommenderConfig, RecommendationsResponse } from "./types";

/**
 * `recommendations/selectors` — the store's recommender state, read through the engine version.
 *
 * These are the only functions in the flow that know the shape of `recommenderStore`. Everything
 * downstream takes plain values, which is what lets the pure layers be tested without a store and
 * lets a surface that keeps its suggestions somewhere else reuse the rest.
 *
 * ⚠️ Every one is a FUNCTION OF STATE, never a module-level read — nothing here runs at
 * module-eval time (audit checks H/I/J).
 */

/**
 * Stable empty fallbacks.
 *
 * 🔴 **Not `|| {}`.** These selectors are passed straight to `useSelector`, which compares the
 * previous and next results by REFERENCE. A fresh object literal on every call is never equal to
 * the last one, so a store with no recommender state yet would re-render the subscriber on every
 * single dispatch in the app.
 */
const EMPTY_CONFIG: RecommenderConfig = {};
const EMPTY_RECOMMENDATIONS: RecommendationsResponse = {};

export function selectRecommenderConfig(state: any): RecommenderConfig {
  return state?.recommenderStore?.recommenderConfig || EMPTY_CONFIG;
}

export function selectRecommendations(state: any): RecommendationsResponse {
  return state?.recommenderStore?.recommendations || EMPTY_RECOMMENDATIONS;
}

export function selectRecommendationId(state: any): string | undefined {
  return state?.recommenderStore?.recommendations?.recommendationId;
}

export function selectIsFetchingRecommendations(state: any): boolean {
  return Boolean(state?.recommenderStore?.fetchingRecommendations);
}

/**
 * Is suggestive selling switched on for this account?
 *
 * Two different questions behind one name, chosen by the live engine version: under V2 it is a
 * single business-data boolean; under V1 it is whether the recommender config asks for either
 * input.
 */
export function isSuggestiveSellingEnabled(state: any): boolean {
  if (getRecommendationEngineVersion() === ENGINE_V2) {
    return Boolean(state?.businessStore?.businessData?.isRistaAIEnabled);
  }
  const recommenderConfig = selectRecommenderConfig(state);
  return Boolean(recommenderConfig.itemIdRequired || recommenderConfig.phoneNumberRequired);
}

/** Which recommendation provider is active, or `undefined` when the feature is off. */
export function getSuggestiveSellingProvider(state: any): string | undefined {
  if (!isSuggestiveSellingEnabled(state)) {
    return undefined;
  }
  if (getRecommendationEngineVersion() === ENGINE_V2) {
    return RISTA_AI_V2_PROVIDER;
  }
  return selectRecommenderConfig(state).provider;
}

/**
 * Can this terminal show suggestions RIGHT NOW?
 *
 * Enabled for the account AND online — the engine is a network call with no offline equivalent,
 * so an offline terminal must not render a widget that can only ever spin.
 */
export function isSuggestiveSellingAvailable(state: any): boolean {
  return isSuggestiveSellingEnabled(state) && !isOffline();
}

/**
 * V1 only: does the engine need an item in the cart before it can answer?
 *
 * Drives the "add an item first" message — under V2 the engine answers on an empty cart, so the
 * message would be a lie.
 */
export function isCartRequiredForRecommendations(state: any): boolean {
  if (getRecommendationEngineVersion() === ENGINE_V2) return false;
  return Boolean(selectRecommenderConfig(state).itemIdRequired);
}
